import React, { useState, useEffect, useRef } from 'react';
import "./usernavbar.css";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AOS from 'aos';
import 'aos/dist/aos.css';

const notifyB = (msg) => toast.success(msg);

const UserNavbar = ({ location, setLocation, locationName, setLocationName }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Location picker state
  const [locOpen, setLocOpen] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [locResults, setLocResults] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const locRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  useEffect(() => {
    const check = () => setIsLoggedIn(!!localStorage.getItem("nearbeusertoken"));
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  // Debounced Nominatim search
  useEffect(() => {
    if (!locSearch.trim()) { setLocResults([]); return; }
    const timer = setTimeout(async () => {
      setLocLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locSearch)}&format=json&limit=6&addressdetails=1&countrycodes=in`
        );
        const data = await res.json();
        setLocResults(data);
      } catch { setLocResults([]); }
      setLocLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [locSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (locRef.current && !locRef.current.contains(e.target)) setLocOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const getCityName = (r) =>
    r.address?.city || r.address?.town || r.address?.village || r.address?.suburb || r.display_name.split(',')[0];

  const getStateName = (r) => r.address?.state || '';

  const selectLocation = (r) => {
    const city = getCityName(r);
    setLocation({ lat: parseFloat(r.lat), lon: parseFloat(r.lon) });
    setLocationName(city);
    setLocOpen(false);
    setLocSearch('');
    setLocResults([]);
  };

  const useGPS = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude, lon: longitude });
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || data.address?.suburb || 'Near You';
          setLocationName(city);
        } catch { setLocationName('Near You'); }
        setLocLoading(false);
        setLocOpen(false);
      },
      () => { setLocLoading(false); toast.error('Location access denied'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("nearbeusertoken");
    navigate("/userlogin");
    notifyB("Logout Successfully");
    setMenuOpen(false);
  };

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <div className="usernavbar">
      <div className="nav-logo" onClick={() => goTo("/")}>
        <div className="nav-wordmark">
          <span className="nav-near">near</span><span className="nav-beee">beee</span>
        </div>
        <div className="nav-dots">
          <span className="nav-dot"></span>
          <span className="nav-dot nav-dot-sm"></span>
        </div>
      </div>

      <div className={`user_btn ${menuOpen ? "slide-in" : ""}`}>

        {/* Location Picker */}
        <div className="loc-chip-wrap" ref={locRef}>
          <button className="loc-chip" onClick={() => setLocOpen(o => !o)}>
            📍 {locationName || 'Set Location'}
            <span className="loc-caret">▾</span>
          </button>
          {locOpen && (
            <div className="loc-dropdown">
              <input
                className="loc-search-input"
                type="text"
                placeholder="Search city or area..."
                value={locSearch}
                onChange={e => setLocSearch(e.target.value)}
                autoFocus
              />
              {locLoading && <p className="loc-loading">Searching...</p>}
              {locResults.map((r, i) => (
                <div key={i} className="loc-result" onClick={() => selectLocation(r)}>
                  <span className="loc-result-city">📍 {getCityName(r)}</span>
                  <span className="loc-result-state">{getStateName(r)}</span>
                </div>
              ))}
              {!locLoading && locSearch && locResults.length === 0 && (
                <p className="loc-no-result">No results found</p>
              )}
              <button className="loc-gps-btn" onClick={useGPS}>
                🎯 Use My GPS Location
              </button>
            </div>
          )}
        </div>

        <button onClick={() => goTo("/userdashboard/explore")}>Offers</button>
        <button onClick={() => goTo("/userdashboard/category")}>Category</button>
        <button onClick={() => goTo("/userdashboard/malls")}>🏬 Malls</button>
        <button onClick={() => goTo("/userdashboard/services")}>Services</button>
        <button onClick={() => goTo("/userdashboard/ni")}>✨ NI</button>
        {isLoggedIn && (
          <button onClick={() => goTo("/userdashboard/wallet")}>🏦 Wallet</button>
        )}

        {isLoggedIn ? (
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        ) : (
          <div
            className="login-container"
            onMouseEnter={() => setShowLoginModal(true)}
            onMouseLeave={() => setShowLoginModal(false)}
          >
            <button className="userlogin-btn">Login</button>
            {showLoginModal && (
              <div className="login-modal">
                <p onClick={() => goTo("/userlogin")}>Login as User</p>
                <p onClick={() => goTo("/shoplogin")}>Login as Shop Owner</p>
                <p onClick={() => goTo("/providerlogin")}>Login as Provider</p>
              </div>
            )}
          </div>
        )}

      </div>
      <div className='barbtn'>
        <button onClick={() => setMenuOpen(!menuOpen)}><i className="fa-solid fa-bars"></i></button>
      </div>
    </div>
  );
};

export default UserNavbar;
